import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { z } from 'zod';
import { useListAvailableUserProfiles } from '@/api/auth';
import { useListTenants } from '@/api/trafikportalen';
import { ory } from '@/auth';
import { CenteredColumn } from '@/components/CenteredColumn';
import { Loading } from '@/components/Loading';
import { ProfilePicker } from '@/components/ProfilePicker';
import Button from '@/components/core/Button';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useConfig, useRoleIds } from '@/contexts/ConfigContext';
import { ThemeCtx } from '@/contexts/ThemeContext';
import {
  findProfileToRestore,
  getInactivityProfile,
  ProfileSelection,
} from '@/helpers/inactivityProfile';
import { isProfileInvalid } from '@/helpers/invalidProfiles';
import { getSelectableUserProfiles } from '@/helpers/userProfiles';
import { useChangeProfile } from '@/hooks/useChangeProfile';
import { useFavicon } from '@/hooks/useFavicon';
import { toast } from '@/hooks/useToast';
import { IconExternalLink, IconLogout } from '@/icons';
import { useAuth } from '@/stores/auth';

const profilesSearchSchema = z.object({
  tenantId: z.string().optional(),
  returnTo: z.string().optional(),
});

export const Route = createFileRoute('/profiles')({
  component: Profile,
  validateSearch: zodSearchValidator(profilesSearchSchema),
});

function Profile() {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { userProfile, logout } = useAuth();
  const changeProfile = useChangeProfile();
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [, setTenantTheme] = useContext(ThemeCtx);
  useFavicon();
  const config = useConfig();
  const roleIds = useRoleIds();
  const { tenantId: targetTenantId, returnTo } = Route.useSearch();
  const { data: profilesRaw, isLoading: profilesLoading } =
    useListAvailableUserProfiles({
      application_uri: config.auth.applicationUri,
    });
  const { data: tenants, isLoading: tenantsLoading } = useListTenants();

  useEffect(() => {
    setTenantTheme('default');
  }, [setTenantTheme]);

  // Skip the picker when there is effectively only a single profile to choose
  // from. When the user is signing in to a specific tenant we only consider
  // that tenant's profiles, so a single profile for the current tenant is
  // auto-selected even if the user has other profiles elsewhere.
  //
  // Only auto-select when no profile is selected yet, which also prevents
  // infinite loops when we are redirected back here due to an invalid profile.
  const autoSelect = useMemo((): ProfileSelection | null => {
    if (!profilesRaw || userProfile) return null;

    const selectable = getSelectableUserProfiles(
      profilesRaw,
      tenants ?? [],
      roleIds,
    ).filter((profile) => !isProfileInvalid(profile.id));

    const restored = findProfileToRestore(
      getInactivityProfile(),
      profilesRaw.filter((profile) => !isProfileInvalid(profile.id)),
      selectable,
      targetTenantId,
    );
    if (restored) {
      return restored;
    }

    if (targetTenantId) {
      const tenantProfiles = selectable.filter(
        (profile) => profile.tenant_id === targetTenantId,
      );
      if (tenantProfiles.length === 1) {
        return { profile: tenantProfiles[0] };
      }
    }

    return selectable.length === 1 ? { profile: selectable[0] } : null;
  }, [profilesRaw, tenants, roleIds, targetTenantId, userProfile]);

  useEffect(() => {
    if (!autoSelect) return;

    // Selecting the profile clears autoSelect before the navigation
    // completes, so remember that we are on our way out.
    setIsAutoSelecting(true);
    const { profile, ...restore } = autoSelect;
    void changeProfile(profile, { ...restore, returnTo });
  }, [autoSelect, changeProfile, returnTo]);

  const handleLogout = useCallback(async () => {
    logout();
    try {
      const { data } = await ory.createBrowserLogoutFlow({
        returnTo: `${window.location.origin}/`,
      });
      window.location.replace(data.logout_url);
    } catch (err) {
      console.error(err);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Utloggningen misslyckades',
          }),
        },
        { autoClose: 3000 },
      );
      navigate({ to: '/' });
    }
  }, [logout, formatMessage, navigate]);

  const { disabled } = useConfig();

  if (disabled) {
    return null;
  }

  if (profilesLoading) {
    return <Loading />;
  }

  if (!profilesRaw || profilesRaw.length === 0) {
    return (
      <>
        <DocumentHead
          titleKey="page.profiles.noProfiles.title"
          descriptionKey="page.profiles.noProfiles.description"
          appendAppName={false}
        />
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded focus:bg-background-50 focus:p-4 focus:text-text-950 focus:shadow-lg"
        >
          <FormattedMessage defaultMessage="Hoppa till huvudinnehåll" />
        </a>
        <main
          id="main-content"
          tabIndex={-1}
          className="flex w-full flex-col items-center gap-4 p-4"
        >
          <h1 className="text-6xl">
            <FormattedMessage defaultMessage="Dina profiler" />
          </h1>
          <div className="flex flex-col items-center gap-2 text-lg">
            <span>
              <FormattedMessage defaultMessage="Det finns inga profiler kopplade till ditt konto." />
            </span>
            <span>
              <FormattedMessage defaultMessage="Kontakta reseansvariga på företaget du ska ha tillgång till." />
            </span>
            <Button to="/" className="mx-auto mt-5">
              <FormattedMessage defaultMessage="Till startsidan" />
            </Button>
          </div>
        </main>
      </>
    );
  }

  // Wait for tenants before deciding whether to show the picker, and keep the
  // loader visible while auto-selecting so we never flash a picker that only
  // has a single option.
  if (tenantsLoading || autoSelect || isAutoSelecting) {
    return <Loading />;
  }

  return (
    <>
      <DocumentHead
        titleKey="page.profiles.title"
        descriptionKey="page.profiles.description"
        appendAppName={false}
      />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded focus:bg-background-50 focus:p-4 focus:text-text-950 focus:shadow-lg"
      >
        <FormattedMessage defaultMessage="Hoppa till huvudinnehåll" />
      </a>
      <main
        id="main-content"
        tabIndex={-1}
        className="flex w-full flex-col items-center gap-8 p-4"
      >
        <h1>
          <FormattedMessage defaultMessage="Välj profil" />
        </h1>

        <CenteredColumn>
          <ProfilePicker returnTo={returnTo} />
        </CenteredColumn>

        <Button
          {...(userProfile
            ? {
                to: '/$tenantId/logout' as const,
                params: { tenantId: userProfile.tenant_id },
              }
            : { onClick: handleLogout })}
          startIcon={<IconLogout />}
          variant="outline"
        >
          <FormattedMessage defaultMessage="Logga ut" />
        </Button>

        <a
          href="/accessibility"
          target="_blank"
          rel="noopener noreferrer"
          className="mx-auto mt-5 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-text-950 hover:bg-background-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600"
        >
          <FormattedMessage defaultMessage="Tillgänglighetsredogörelse" />
          <IconExternalLink className="ml-2 size-4" aria-hidden="true" />
          <span className="sr-only">
            <FormattedMessage defaultMessage=" (öppnas i ny flik)" />
          </span>
        </a>
      </main>
    </>
  );
}
